import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Mail, Plus, RefreshCw, Loader2, Trash2, Link2, UserPlus, CheckSquare, CalendarClock, ShieldCheck,
} from "lucide-react";

const EMPTY_ACCOUNT = {
  provider: "imap", email: "", imap_host: "mail.privateemail.com", imap_port: 993, imap_ssl: true,
  smtp_host: "", smtp_port: 465, smtp_ssl: true, username: "", password: "",
};
const CRM_STAGES = ["Lead", "Prospect", "Customer", "Past Customer"];
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function Email() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ACCOUNT);
  const [connecting, setConnecting] = useState(false);
  const [account, setAccount] = useState(null);
  const [messages, setMessages] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [stale, setStale] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);
  const [crmRecords, setCrmRecords] = useState([]);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/email/accounts");
      setAccounts(r.data || []);
      if ((r.data || []).length) setAccount((prev) => prev || r.data[0]);
    } catch { toast.error("Could not load your email accounts."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => {
    api.get("/crm").then((r) => setCrmRecords(r.data || [])).catch(() => {});
  }, []);

  const connect = async () => {
    if (!form.email || !form.imap_host || !form.username || !form.password) {
      toast.error("Email, IMAP host, username and password are required."); return;
    }
    setConnecting(true);
    try {
      const r = await api.post("/email/accounts", form);
      toast.success("Email account connected.");
      setConnectOpen(false); setForm(EMPTY_ACCOUNT);
      setAccounts((a) => [r.data, ...a]); setAccount(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not connect. Check your settings."); }
    finally { setConnecting(false); }
  };

  const fetchMessages = useCallback(async (acc) => {
    if (!acc) return;
    setFetching(true);
    try {
      const r = await api.get(`/email/accounts/${acc.id}/messages`, { params: { limit: 25 } });
      setMessages(r.data?.messages || []);
      setStale(!!r.data?.stale);
      if (r.data?.stale) toast.warning("Couldn't reach the mail server. Showing the last synced messages.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not retrieve emails."); }
    finally { setFetching(false); }
  }, []);

  useEffect(() => { if (account) fetchMessages(account); }, [account, fetchMessages]);

  const openMessage = async (m) => {
    setSelected(m); setDetail(null); setLoadingDetail(true);
    try { const r = await api.get(`/email/messages/${m.id}`); setDetail(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not open this email."); }
    finally { setLoadingDetail(false); }
  };

  const disconnect = async () => {
    try {
      await api.delete(`/email/accounts/${disconnecting.id}`);
      toast.success("Email account disconnected.");
      const remaining = accounts.filter((a) => a.id !== disconnecting.id);
      setAccounts(remaining); setAccount(remaining[0] || null);
      setMessages([]); setSelected(null); setDetail(null); setDisconnecting(null);
    } catch { toast.error("Could not disconnect the account."); }
  };

  if (loading) {
    return (
      <div>
        <PageHeader testid="email-header" title="Email" subtitle="Connect an inbox and turn emails into CRM records, tasks and follow-ups" />
        <Card className="rounded-3xl p-12 text-center text-muted-foreground" data-testid="email-loading">Loading…</Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        testid="email-header"
        title="Email"
        subtitle="Connect an inbox and turn emails into CRM records, tasks and follow-ups"
        action={
          accounts.length > 0 ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => fetchMessages(account)} disabled={fetching} className="rounded-full gap-2" data-testid="email-refresh-button">
                {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
              </Button>
              <Button onClick={() => setConnectOpen(true)} className="rounded-full gap-2 bg-primary hover:bg-primary/90" data-testid="email-add-account-button">
                <Plus className="h-4 w-4" /> Add account
              </Button>
            </div>
          ) : null
        }
      />

      {accounts.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-2 border-border p-12 text-center" data-testid="email-empty">
          <Mail className="h-10 w-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground mb-1">No email account connected yet.</p>
          <p className="text-sm text-muted-foreground mb-5">Connect a standard IMAP mailbox (for example Namecheap Private Email) to get started. Read-only for now.</p>
          <Button onClick={() => setConnectOpen(true)} className="rounded-full gap-2 bg-primary hover:bg-primary/90" data-testid="email-connect-button">
            <Plus className="h-4 w-4" /> Connect email account
          </Button>
        </Card>
      ) : (
        <>
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <Select value={account?.id || ""} onValueChange={(id) => { setAccount(accounts.find((a) => a.id === id)); setSelected(null); setDetail(null); }}>
              <SelectTrigger className="rounded-full w-full sm:w-80" data-testid="email-account-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.email}</SelectItem>)}
              </SelectContent>
            </Select>
            {account && (
              <>
                <Badge className={`rounded-full ${account.status === "connected" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`} data-testid="email-account-status">{account.status}</Badge>
                <Button variant="ghost" size="sm" className="rounded-full text-destructive gap-2" onClick={() => setDisconnecting(account)} data-testid="email-disconnect-button">
                  <Trash2 className="h-4 w-4" /> Disconnect
                </Button>
              </>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="rounded-3xl border-border shadow-soft p-2 sm:p-3" data-testid="email-message-list">
              {stale && (
                <div className="m-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="email-stale-banner">
                  Couldn't refresh from the mail server. Showing the last synced messages.
                </div>
              )}
              {fetching ? (
                <div className="p-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Retrieving…</div>
              ) : messages.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground">No messages found in this inbox.</div>
              ) : (
                <div className="divide-y divide-border">
                  {messages.map((m) => (
                    <button key={m.id} onClick={() => openMessage(m)} data-testid={`email-message-${m.id}`}
                      className={`w-full text-left px-3 py-3 rounded-2xl transition-colors hover:bg-muted ${selected?.id === m.id ? "bg-muted" : ""}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm truncate">{m.from_name || m.from_addr || "(unknown sender)"}</span>
                        <span className="text-xs text-muted-foreground shrink-0">{fmtDate(m.date)}</span>
                      </div>
                      <p className="text-sm truncate mt-0.5">{m.subject || "(no subject)"}</p>
                      {(m.linked || []).length > 0 && (
                        <Badge className="mt-1.5 rounded-full bg-primary/10 text-primary text-[10px]" data-testid={`email-linked-${m.id}`}>
                          <Link2 className="h-3 w-3 mr-1" /> Linked
                        </Badge>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </Card>

            <Card className="rounded-3xl border-border shadow-soft p-5" data-testid="email-detail-panel">
              {!selected ? (
                <div className="p-8 text-center text-muted-foreground text-sm">Select an email to read it and turn it into a CRM record, task or follow-up.</div>
              ) : loadingDetail ? (
                <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
              ) : detail ? (
                <div>
                  <h3 className="font-heading text-lg leading-tight" data-testid="email-detail-subject">{detail.subject || "(no subject)"}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{detail.from_name} &lt;{detail.from_addr}&gt;</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(detail.date)}</p>
                  <div className="mt-4 max-h-72 overflow-y-auto text-sm whitespace-pre-wrap text-foreground/80 border-t border-border pt-4" data-testid="email-detail-body">
                    {detail.body_text || (detail.body_html ? detail.body_html.replace(/<[^>]+>/g, " ") : detail.snippet || "(no content)")}
                  </div>
                  <div className="mt-5 border-t border-border pt-4">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Turn this email into</p>
                    <EmailActions email={detail} crmRecords={crmRecords}
                      onDone={() => { fetchMessages(account); toast.success("Done."); }} />
                  </div>
                </div>
              ) : null}
            </Card>
          </div>
        </>
      )}

      {/* Connect account dialog */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="email-connect-dialog">
          <DialogHeader>
            <DialogTitle>Connect an email account</DialogTitle>
            <DialogDescription>Standard IMAP over SSL. Read-only. Your password is encrypted and never shared.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Email address</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@yourdomain.com" data-testid="email-form-email" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>IMAP host</Label>
                <Input value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} placeholder="mail.privateemail.com" data-testid="email-form-imap-host" />
              </div>
              <div className="space-y-1.5">
                <Label>Port</Label>
                <Input type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value) || 993 })} data-testid="email-form-imap-port" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <Label className="text-sm">Use SSL/TLS</Label>
              <Switch checked={form.imap_ssl} onCheckedChange={(v) => setForm({ ...form, imap_ssl: v })} data-testid="email-form-imap-ssl" />
            </div>
            <div className="space-y-1.5">
              <Label>Username</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="Usually your full email address" data-testid="email-form-username" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mailbox password or app password" data-testid="email-form-password" />
            </div>
            <div className="flex items-start gap-2 rounded-xl bg-muted/40 p-3 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
              <span>We validate the connection before saving. Some providers (like Gmail) require an app password or aren't supported via IMAP yet.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={connect} disabled={connecting} className="rounded-full bg-primary hover:bg-primary/90" data-testid="email-connect-submit">
              {connecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connecting…</> : "Connect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!disconnecting} onOpenChange={(o) => !o && setDisconnecting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect this email account?</AlertDialogTitle>
            <AlertDialogDescription>{disconnecting?.email} will be disconnected and its stored credentials and synced emails removed. CRM records and tasks you already created stay.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={disconnect} className="rounded-full bg-destructive hover:bg-destructive/90" data-testid="email-disconnect-confirm">Disconnect</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmailActions({ email, crmRecords, onDone }) {
  const [busy, setBusy] = useState("");
  const [crmId, setCrmId] = useState("");
  const [followUp, setFollowUp] = useState(todayStr());

  const run = async (action, extra = {}) => {
    setBusy(action);
    try {
      await api.post(`/email/messages/${email.id}/action`, { action, ...extra });
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not complete that action."); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" className="rounded-full gap-2" disabled={!!busy} onClick={() => run("create_crm", { stage: "Lead" })} data-testid="email-action-create-crm">
          <UserPlus className="h-4 w-4" /> New CRM lead
        </Button>
        <Button size="sm" variant="outline" className="rounded-full gap-2" disabled={!!busy} onClick={() => run("create_task")} data-testid="email-action-create-task">
          <CheckSquare className="h-4 w-4" /> New task
        </Button>
      </div>

      <div className="rounded-xl border border-border p-3 space-y-2">
        <Label className="text-xs">Link to an existing CRM record</Label>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={crmId} onValueChange={setCrmId}>
            <SelectTrigger className="rounded-full w-full sm:w-56 h-9" data-testid="email-action-crm-select"><SelectValue placeholder="Choose a record" /></SelectTrigger>
            <SelectContent>
              {crmRecords.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.org_name ? ` — ${r.org_name}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="rounded-full gap-2" disabled={!crmId || !!busy} onClick={() => run("link_crm", { crm_id: crmId })} data-testid="email-action-link-crm">
            <Link2 className="h-4 w-4" /> Link
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="rounded-full h-9 w-40" data-testid="email-action-followup-date" />
          <Button size="sm" variant="outline" className="rounded-full gap-2" disabled={!crmId || !followUp || !!busy} onClick={() => run("create_followup", { crm_id: crmId, follow_up_date: followUp })} data-testid="email-action-followup">
            <CalendarClock className="h-4 w-4" /> Set follow-up
          </Button>
        </div>
      </div>
      {busy && <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Working…</p>}
    </div>
  );
}
